import React, { useEffect, useState, useMemo } from 'react';
import { Card, Col, Row, Form, Button } from '@themesberg/react-bootstrap';
import Table from 'react-bootstrap-table-next';
import EmptyTable from '../../components/common/EmptyTableView';
import { returnRowBGColor } from '../../utils/parcels';
import { useDispatch, useSelector } from 'react-redux';
import { setPageNumberInStore } from '../../actions/tablePage';
import Pagination from './Pagination';
import Fuse from 'fuse.js';

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

const TripsTableData = ({ page, columns, data = [], onRowClick }) => {
  const dispatch = useDispatch();
  const id = `${page}-table`;

  const safeData = useMemo(
    () => Object.values(data || {}).map((item) => item || {}),
    [data]
  );

  const fuse = useMemo(() => {
    return new Fuse(safeData, {
      keys: columns.map((col) => col.dataField),
      threshold: 0,
      ignoreLocation: true,
    });
  }, [safeData, columns]);

  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = useMemo(() => {
    if (!searchTerm) return safeData;
    return fuse.search(searchTerm).map((r) => r.item);
  }, [searchTerm, fuse, safeData]);

  const storedPage = useSelector((s) => s.pageNumber?.[id] || 1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [currentPage, setCurrentPage] = useState(storedPage);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(setPageNumberInStore({ id, page: 1 }));
  }, [itemsPerPage, searchTerm, dispatch, id]);

  const handlePageChange = (pg) => {
    setCurrentPage(pg);
    dispatch(setPageNumberInStore({ id, page: pg }));
  };

  const total = filteredData.length;
  const start = (currentPage - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  const pageData = filteredData.slice(start, end);

  return (
    <Card>
      <Card.Header>
        <Row className="align-items-center">
          <Col md="4" className="d-flex gap-2 mb-2 mb-md-0">
            <Form.Control
              type="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Button onClick={() => setSearchTerm('')}>clear</Button>
          </Col>
        </Row>
      </Card.Header>

      <Table
        keyField="id"
        data={pageData}
        columns={columns}
        rowClasses={(r) =>
          `${returnRowBGColor(r.status)} border-bottom cursor-hand`
        }
        {...(onRowClick && {
          rowEvents: {
            onClick: (_, row) => onRowClick(row.tripNumber, row.objectId),
          },
        })}
        noDataIndication={<EmptyTable />}
        wrapperClasses="table-responsive"
        bodyClasses="border-0"
        classes="table-flush dataTable-table"
      />
      <Card.Footer>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              className="w-auto"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Form.Select>
          </div>
          <div>
            {`Showing ${start + 1} to ${Math.min(
              end,
              total
            )} of ${total} entries`}
          </div>
          <Pagination
            total={total}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </div>
      </Card.Footer>
    </Card>
  );
};

export default TripsTableData;
